import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Skeleton,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MigrationJob, MigrationJobStatus } from 'types/apis/migrationInterfaces';

interface MigrationJobListProps {
  jobs: MigrationJob[];
  loading: boolean;
  onJobSelect: (job: MigrationJob) => void;
  onRefresh: () => void;
}

const getStatusIcon = (status: MigrationJobStatus) => {
  switch (status) {
    case 'COMPLETED':
      return <CheckCircleIcon color="success" />;
    case 'FAILED':
      return <ErrorIcon color="error" />;
    case 'RUNNING':
      return <PlayCircleIcon color="primary" />;
    case 'PENDING':
    default:
      return <HourglassEmptyIcon color="action" />;
  }
};

const getStatusChip = (status: MigrationJobStatus) => {
  const colorMap: Record<MigrationJobStatus, 'success' | 'error' | 'primary' | 'default'> = {
    COMPLETED: 'success',
    FAILED: 'error',
    RUNNING: 'primary',
    PENDING: 'default',
  };
  return (
    <Chip 
      label={status} 
      size="small" 
      color={colorMap[status]} 
      variant="outlined"
    />
  );
};

const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

export const MigrationJobList: React.FC<MigrationJobListProps> = ({
  jobs,
  loading,
  onJobSelect,
  onRefresh,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={72} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (jobs.length === 0) {
    return (
      <Box sx={{ 
        p: 4, 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}>
        <Typography variant="body1" color="text.secondary">
          No migration jobs found for this pilot.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Start a new import to migrate recommendations from Carrom.
        </Typography>
        <IconButton onClick={onRefresh} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        px: 2,
        py: 1,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Typography variant="subtitle2" color="text.secondary">
          {jobs.length} job{jobs.length !== 1 ? 's' : ''} found
        </Typography>
        <IconButton onClick={onRefresh} size="small" title="Refresh">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>
      <List sx={{ p: 0 }}>
        {jobs.map((job) => (
          <ListItem key={job.jobId} disablePadding divider>
            <ListItemButton onClick={() => onJobSelect(job)} sx={{ py: 2 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {getStatusIcon(job.jobStatus)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" fontWeight={500}>
                      {job.jobId}
                    </Typography>
                    {getStatusChip(job.jobStatus)}
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Progress: {job.progress}%
                    </Typography>
                    {job.result?.stats && (
                      <Typography variant="caption" color="text.secondary">
                        • {job.result.stats.totalRecommendations} recos
                        ({job.result.stats.created} new, {job.result.stats.updated} updated)
                      </Typography>
                    )}
                    {job.createdAt && (
                      <Typography variant="caption" color="text.secondary">
                        • {formatDate(job.createdAt)}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <ChevronRightIcon color="action" />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

